class MMCartRemoveButton extends HTMLElement {
    constructor() {
      super();
  
      this.addEventListener('click', (event) => {
        event.preventDefault();
        const cartItems = this.closest('mm-cart-items') || this.closest('mm-cart-drawer-items');
        cartItems?.updateQuantity(this.dataset.index, 0, null, this);
      });
    }
}
  
customElements.define('mm-cart-remove-button', MMCartRemoveButton);

class MMCartItems extends HTMLElement {
    cartUpdateUnsubscriber = undefined;

    constructor() {
        super();

        this.init();
        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    }

    init() {
        this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

        // Progress Bar
        this.bxgyBar = document.getElementById('bxgy-progress');
        this.giftsIds = new Set();
        this.bxgyBar?.querySelectorAll('.js-promotion-item').forEach(item => {
            const productId = item.getAttribute('data-product-id');
            if (productId) this.giftsIds.add(Number(productId));
        });

        // Upsell Products
        this.upsellProducts = document.querySelectorAll('.cart-upsell-wrapper .upsell-product-item');
        const jsonText = document.querySelector('.cart-upsell-wrapper .js-upsell-products-json')?.textContent;
        this.upsellProductsJson = jsonText ? JSON.parse(jsonText) : null;

       this.initCartJSON();
       this.handleUpsellProducts();

       this.addEventListener('change', (event) => {
           if (event.target.classList.contains('js-subscribe-toggle')) {
               this.onItemSubscribeToggle(event.target);
           }
       });
    }

    async getCartJSON() {
        const response = await fetch('/cart.js');
        if (!response.ok) {
          throw new Error('Failed to fetch cart');
        }
        return response.json();
    }

    async initCartJSON() {
        try {
            const cart = await this.getCartJSON();
            this.filterUpsellProducts(cart);
            this.updateProgressBar(cart);
            this.renderDiscountSummary(cart);
        } catch (error) {
          console.error('Error initCartJSON():', error);
        }
    }

    updateProgressBar(cartJson, reconcile = true) {
        // Re-resolve in case the element was re-rendered since init.
        const container = document.getElementById('bxgy-progress');
        this.bxgyBar = container;
        const elements = {
            container,
            fill: container?.querySelector('#progressFill'),
            message: container?.querySelector('#bxgy-progress-message')
        };

        if (!elements.container || !elements.fill || !cartJson) return;

        const promotionItems = elements.container.querySelectorAll('.js-promotion-item');
        if (!promotionItems.length) return;

        // Basis for price milestones: measure against the item total BEFORE
        // discounts ('original') or the discounted cart total ('discounted').
        const basis = elements.container.getAttribute('data-progress-basis') || 'original';
        const useDiscounted = basis === 'discounted';

        // Only exclude gift lines that are actually FREE (fully discounted to 0).
        // A reward added at full price is real spend and must count toward the
        // milestones, otherwise the bar collapses even though the cart total is high.
        const giftItems = (cartJson.items || []).filter(
            i => i.properties?._progressbar_promotion && i.final_line_price === 0
        );
        const giftCount = giftItems.reduce((sum, i) => sum + (i.quantity || 0), 0);

        let totalSource;
        let giftDeduction;
        if (useDiscounted) {
            // total_price is already after all discounts (and free gifts are 0),
            // so no extra gift deduction is needed.
            totalSource = typeof cartJson.total_price === 'number'
                ? cartJson.total_price
                : (cartJson.original_total_price || 0);
            giftDeduction = 0;
        } else {
            // original_total_price includes the gift's full price, so subtract it.
            totalSource = typeof cartJson.original_total_price === 'number'
                ? cartJson.original_total_price
                : (cartJson.total_price || 0);
            giftDeduction = giftItems.reduce((sum, i) => sum + (i.original_line_price || 0), 0);
        }

        const cartTotal = Math.max(totalSource - giftDeduction, 0) / 100;
        const cartItemCount = Math.max((cartJson.item_count || 0) - giftCount, 0);
        const valueForType = (type) => (type === 'items' ? cartItemCount : cartTotal);

        const thresholds = Array.from(promotionItems)
            .map(item => {
                const thresholdType = this.readElementAttr(item, 'data-threshold-type') || 'price';
                return {
                    element: item,
                    threshold: this.readElementAttr(item, 'data-threshold', true),
                    thresholdType,
                    currentValue: valueForType(thresholdType),
                    productId: this.readElementAttr(item, 'data-product-id', true),
                    discountCode: this.readElementAttr(item, 'data-discount-code')
                };
            })
            .filter(t => t.threshold !== null && t.threshold > 0);

        if (!thresholds.length) return;

        // Reset any inline positioning so the milestones lay out via CSS, then
        // measure their real centers to anchor the fill precisely under each one.
        thresholds.forEach((tier) => {
            tier.element.style.position = '';
            tier.element.style.left = '';
            tier.element.style.transform = '';
        });

        const promotionWrapper = elements.container.querySelector('.promotion-wrapper');
        const trackRect = elements.fill.parentElement.getBoundingClientRect();
        const promotionRect = (promotionWrapper || elements.fill.parentElement).getBoundingClientRect();
        const canMeasure = trackRect.width > 0 && promotionRect.width > 0;

        thresholds.forEach((tier, index) => {
            const itemRect = tier.element.getBoundingClientRect();
            const itemCenterX = itemRect.left + itemRect.width / 2;
            const offsetFromTrackLeft = itemCenterX - trackRect.left;
            const fallback = ((index + 0.5) / thresholds.length) * 100;
            const measured = canMeasure
                ? (offsetFromTrackLeft / trackRect.width) * 100
                : fallback;
            tier.visualPosition = Math.max(0, Math.min(100, measured));
        });

        thresholds.sort((a, b) => a.visualPosition - b.visualPosition);

        // Build the DESIRED promo state from the milestones. We reconcile this
        // against the actual cart instead of using edge-detection on the
        // `.reached` CSS class: that class is wiped every time the section
        // re-renders, so the old apply/remove-on-transition logic would
        // apply a discount and then immediately remove it during the cascade of
        // cart updates. State reconciliation is idempotent, so a reached tier's
        // discount stays applied and an unreached tier's discount stays removed.
        const reachedCodes = [];
        const unreachedCodes = [];
        const reachedProducts = [];
        const unreachedProducts = [];

        thresholds.forEach(tier => {
            const isReached = tier.currentValue >= tier.threshold;
            tier.isReached = isReached;
            tier.element.classList.toggle('reached', isReached);

            if (isReached) {
                if (tier.discountCode) reachedCodes.push(tier.discountCode);
                if (tier.productId) reachedProducts.push(tier.productId);
            } else {
                if (tier.discountCode) unreachedCodes.push(tier.discountCode);
                if (tier.productId) unreachedProducts.push(tier.productId);
            }
        });

        if (reconcile) {
            this.reconcilePromotions(cartJson, {
                reachedCodes,
                unreachedCodes,
                reachedProducts,
                unreachedProducts
            });
        }

        let progressPercent;
        const allReached = thresholds.every(t => t.isReached);
        if (allReached) {
            progressPercent = 100;
        } else {
            let prevPosition = 0;
            let prevTier = null;
            progressPercent = 0;
            for (let i = 0; i < thresholds.length; i++) {
                const tier = thresholds[i];
                if (!tier.isReached) {
                    let ratio;
                    if (prevTier && prevTier.thresholdType === tier.thresholdType) {
                        const span = tier.threshold - prevTier.threshold;
                        ratio = span > 0
                            ? (tier.currentValue - prevTier.threshold) / span
                            : 0;
                    } else {
                        ratio = tier.threshold > 0 ? tier.currentValue / tier.threshold : 0;
                    }
                    ratio = Math.max(0, Math.min(1, ratio));
                    // Always keep a small visual gap between the fill and the unmet milestone icon.
                    const PRE_THRESHOLD_GAP = 8;
                    const cappedPosition = Math.max(prevPosition, tier.visualPosition - PRE_THRESHOLD_GAP);
                    progressPercent = prevPosition + ratio * (cappedPosition - prevPosition);
                    break;
                }
                prevPosition = tier.visualPosition;
                prevTier = tier;
            }
        }

        elements.fill.style.width = `${Math.max(0, Math.min(100, progressPercent))}%`;
    }

    filterUpsellProducts(cartJson) {
        if (!this.upsellProductsJson || !this.upsellProducts) return;

        const cartVariantIds = new Set(cartJson?.items?.map(item => item.variant_id) || []);
        this.upsellProducts.forEach((upsellEl) => {
            const variantId = upsellEl.getAttribute("data-variant-id");
            if (!variantId) return;

            const upsellEntry = this.upsellProductsJson.find(p => p.variant_id == variantId);
            if (!upsellEntry) return;

            const existsInCart = upsellEntry.variant_ids
                .some(id => cartVariantIds.has(Number(id)));

            upsellEl.classList.toggle("hidden", existsInCart);
        });

        const wrapper = document.querySelector('.cart-upsell-wrapper');
        if (wrapper) {
            const anyVisible = Array.from(this.upsellProducts)
                .some(el => !el.classList.contains('hidden'));
            wrapper.classList.toggle('hidden', !anyVisible);
        }
    }

    handleUpsellProducts() {
        if (!this.upsellProductsJson || !this.upsellProducts.length) return;

        this.upsellProducts.forEach(product => {
            const addButton = product.querySelector('.js-upsell-atc');
            if (!addButton || addButton.dataset.upsellInitialized) return;

            addButton.dataset.upsellInitialized = 'true';

            const variantSelect = product.querySelector('.js-upsell-variant-select');
            if (variantSelect) {
                variantSelect.addEventListener('change', () => {
                    this.onUpsellVariantChange(product, variantSelect, addButton);
                });
            }

            addButton.addEventListener('click', async (event) => {
                event.preventDefault();
                if (addButton.disabled) return;

                addButton.disabled = true;
                addButton.classList.add('active');
                addButton.querySelector('.cart-loader').classList.remove('hidden');

                const variantId = addButton.getAttribute('data-id');
                if (!variantId) return;

                const sellingPlanId = product.getAttribute('data-selling-plan-id');
                const bodyPayload = {
                    id: variantId,
                    quantity: 1,
                    sections: this.getSectionsToRender().map(section => section.section)
                };

                if (sellingPlanId) {
                    bodyPayload.selling_plan = sellingPlanId;
                }

                const body = JSON.stringify(bodyPayload);

                await fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig(), ...{ body } })
                    .then((response) => response.json())
                    .then(async (parsedState) => {
                        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'upsell-cart-items', cart: parsedState });
                    })
                    .catch((error) => {
                        if (error.name === 'AbortError') {
                          console.log('Fetch aborted by user');
                        }
                        else {
                          console.error(error);
                          this.onCartError(error.message);
                        }
                      })
                    .finally(() => {
                        addButton.disabled = false;
                        addButton.classList.remove('active');
                        addButton.querySelector('.cart-loader').classList.add('hidden');
                    });
            });

        });
    }

    onUpsellVariantChange(product, select, addButton) {
        const option = select.options[select.selectedIndex];
        if (!option) return;

        const variantId = option.value;
        const isAvailable = option.getAttribute('data-available') === 'true';

        product.setAttribute('data-variant-id', variantId);
        addButton.setAttribute('data-id', variantId);
        addButton.disabled = !isAvailable;

        const sellingPlanId = option.getAttribute('data-selling-plan-id');
        if (sellingPlanId) {
            product.setAttribute('data-selling-plan-id', sellingPlanId);
        } else {
            product.removeAttribute('data-selling-plan-id');
        }

        const priceEl = product.querySelector('.up-price');
        if (priceEl) priceEl.textContent = option.getAttribute('data-price') || priceEl.textContent;

        const compareEl = product.querySelector('.up-compare-price');
        const hasCompare = option.getAttribute('data-has-compare') === 'true';
        if (compareEl) {
            compareEl.textContent = option.getAttribute('data-compare-price') || '';
            compareEl.hidden = !hasCompare;
        }

        const soldOutEl = product.querySelector('.up-sold-out');
        if (soldOutEl) soldOutEl.hidden = isAvailable;
    }

    disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
            this.cartUpdateUnsubscriber();
        }
    }

    onChange(event) {
        if (event.target.classList.contains('js-subscribe-toggle')) return;
        this.validateQuantity(event);
    }

    onCartUpdate(event) {
        if (event.cart.errors) {
            this.updateLiveRegions(event.line, event.cart.errors);
            return;
        }

        const isEmpty = event.cart.item_count === 0;
        this.handleEmptyCart(isEmpty);
        this.classList.toggle('is-empty', isEmpty);

        if (isEmpty) {
            this.closest('mm-cart-drawer')?.classList.toggle('is-empty', isEmpty);
            this.addEmptyState();
        }

        const sectionsToRender = this.getSectionsToRender();

        sectionsToRender.forEach(section => {
            const container = document.getElementById(section.id);
            if (!container) return;

            const newHTML = event.cart.sections?.[section.section];
            if (!newHTML) return;

            const parsedHTML = this.getSectionInnerHTML(newHTML, section.selector);
            if (!parsedHTML) return;

            const elementsToReplace = section.selector
                ? container.querySelectorAll(section.selector)
                : [container];

            elementsToReplace.forEach(el => {
                el.innerHTML = parsedHTML;
            });
        });

        (async () => {
            let cart;
            try {
                cart = await this.getCartJSON();
            } catch (error) {
                console.error('Error fetching cart for progress bar:', error);
                return;
            }
            try {
                this.filterUpsellProducts(cart);
            } catch (error) {
                console.error('Error filtering upsell products:', error);
            }
            try {
                // Don't re-run promo reconciliation for cart updates that our own
                // promo logic triggered — just refresh the visual bar. This stops
                // the apply -> re-render -> remove cascade.
                const reconcile = event.source !== 'cart-promo';
                this.updateProgressBar(cart, reconcile);
            } catch (error) {
                console.error('Error updating progress bar:', error);
            }
            try {
                this.renderDiscountSummary(cart);
            } catch (error) {
                console.error('Error rendering discount summary:', error);
            }
        })();

        document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: {
                cart: event.cart
            }
        }));
    }

    addEmptyState() {
        const drawerInner = this.closest(".drawer__inner");
        if(!drawerInner) return;

        if (drawerInner.querySelector(".drawer__inner-empty")) {
            return;
        }

        drawerInner.insertAdjacentHTML(
            "beforeend",
            `
            <div class="drawer__inner-empty">
              <div class="cart-drawer__empty-content">
                <h2 class="cart__empty-text">Your cart is empty</h2>
                <button class="drawer__close drawer__close--empty" type="button" aria-label="Close cart" onclick="this.closest('mm-cart-drawer').close()">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                    <path d="M12 12L2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
                </button>
                <a href="/collections/all" class="mm-button">Continue shopping</a>
              </div>
            </div>
            `
        );
    }

    handleEmptyCart(isEmpty) {
        const drawer =this.closest('mm-cart-drawer');
        if(!drawer) return;

        const isEmptyClass = drawer.classList.contains('is-empty');
        if(!isEmpty && isEmptyClass) {
            drawer.classList.remove('is-empty');
            const emptyDiv = drawer?.querySelector('.drawer__inner-empty');
            if(emptyDiv) emptyDiv.remove();
        }
    }

    updateQuantity(line, quantity, name, target) {
        this.enableLoading(line);

        const body = JSON.stringify({
          line,
          quantity,
          sections: this.getSectionsToRender().map(section => section.section)
        });

        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart: parsedState, target, line, name });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
              this.onCartError(error.message);
            }
          })
          .finally(() => {
            this.disableLoading(line);
          });
    }

    resolveSellingPlanIdFromEl(el) {
        if (el.dataset.firstSellingPlanId) {
            return parseInt(el.dataset.firstSellingPlanId, 10);
        }

        const plansJson = el.dataset.sellingPlans;
        if (!plansJson) return null;

        try {
            const plans = JSON.parse(plansJson);
            if (!plans.length) return null;

            const pref = this.dataset.defaultFrequency || 'first';
            if (pref && pref !== 'first') {
                const match = plans.find((plan) => plan.name.includes(pref));
                if (match) return parseInt(match.id, 10);
            }

            return parseInt(plans[0].id, 10);
        } catch (_) {
            return null;
        }
    }

    async onItemSubscribeToggle(toggle) {
        const itemEl = toggle.closest('.mm-cart-item[data-index]');
        if (!itemEl) return;

        const line = parseInt(itemEl.dataset.index, 10);
        const isSubscribed = toggle.checked;
        const sellingPlanId = this.resolveSellingPlanIdFromEl(itemEl);

        if (isSubscribed && !sellingPlanId) {
            toggle.checked = false;
            return;
        }

        const quantityInput = itemEl.querySelector('.quantity__input');
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) : null;

        toggle.disabled = true;
        this.enableLoading(line);

        const body = JSON.stringify({
            line,
            quantity,
            selling_plan: isSubscribed ? sellingPlanId : '',
            sections: this.getSectionsToRender().map((section) => section.section)
        });

        try {
            const response = await fetch(theme.routes.cart_change_url, {
                ...theme.utils.fetchConfig(),
                body
            });
            const parsedState = await response.json();

            if (parsedState.errors) {
                throw new Error(parsedState.errors);
            }

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-items',
                cart: parsedState,
                line
            });
        } catch (error) {
            toggle.checked = !isSubscribed;
            console.error('Error toggling subscription:', error);
        } finally {
            toggle.disabled = false;
            this.disableLoading(line);
        }
    }

    formatMoney(cents, currency) {
        const amount = (Number(cents) || 0) / 100;
        try {
            return new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: currency || 'USD'
            }).format(amount);
        } catch (e) {
            // Fallback if the currency code is unknown to Intl.
            return amount.toFixed(2);
        }
    }

    renderDiscountSummary(cartJson) {
        const container = document.getElementById('cart-discount-summary');
        if (!container) return;

        const currency = cartJson.currency;
        // Order/cart-level discounts (e.g. a % or £ off code applied to the cart).
        const apps = (cartJson.cart_level_discount_applications || []).filter(
            app => (app.total_allocated_amount || 0) > 0
        );

        if (!apps.length) {
            container.hidden = true;
            container.innerHTML = '';
            return;
        }

        const rows = apps.map(app => {
            const amountOff = this.formatMoney(app.total_allocated_amount, currency);

            let offValue;
            if (app.value_type === 'percentage') {
                const pct = parseFloat(app.value);
                const pctLabel = Number.isFinite(pct)
                    ? (pct % 1 === 0 ? pct.toFixed(0) : String(pct))
                    : '';
                offValue = pctLabel ? `${pctLabel}% · ${amountOff}` : amountOff;
            } else {
                offValue = amountOff;
            }

            const code = (app.title || '').toString();

            return (
                '<div class="cart-discount">' +
                    '<div class="cart-discount__row">' +
                        '<span class="cart-discount__label">Off</span>' +
                        '<span class="cart-discount__value">' + this.escapeHtml(offValue) + '</span>' +
                    '</div>' +
                    (code
                        ? '<div class="cart-discount__row">' +
                              '<span class="cart-discount__label">Discount Code</span>' +
                              '<span class="cart-discount__value cart-discount__value--code">' +
                                  this.escapeHtml(code) +
                              '</span>' +
                          '</div>'
                        : '') +
                '</div>'
            );
        }).join('');

        container.innerHTML = rows;
        container.hidden = false;
    }

    escapeHtml(str) {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    reconcilePromotions(cartJson, desired) {
        // --- Discount codes ---------------------------------------------------
        // Shopify discount codes are case-insensitive, and cart.js may echo them
        // back in a different case than the setting/metafield uses. Compare on a
        // normalised key so we don't thrash (re-sending the same codes on every
        // update) while preserving the original casing when we send.
        const norm = (code) => String(code).trim().toLowerCase();

        const managed = new Set(
            [...desired.reachedCodes, ...desired.unreachedCodes].map(norm)
        );
        const unreached = new Set(desired.unreachedCodes.map(norm));
        const currentCodes = (cartJson.discount_codes || []).map(dc => dc.code);

        // Preserve manually-applied codes we don't manage, add every reached
        // milestone code, and drop unreached milestone codes.
        const finalByKey = new Map();
        currentCodes.forEach(code => {
            const key = norm(code);
            if (!managed.has(key)) finalByKey.set(key, code);
        });
        desired.reachedCodes.forEach(code => finalByKey.set(norm(code), code));
        unreached.forEach(key => finalByKey.delete(key));

        const currentKeys = new Set(currentCodes.map(norm));
        const finalKeys = new Set(finalByKey.keys());
        const codesChanged =
            finalKeys.size !== currentKeys.size ||
            [...finalKeys].some(key => !currentKeys.has(key));

        if (codesChanged) {
            this.setCartDiscounts([...finalByKey.values()]);
        }

        // --- Promotional products --------------------------------------------
        desired.reachedProducts.forEach(id => this.addProduct(id, cartJson));
        desired.unreachedProducts.forEach(id => this.removeProduct(id, cartJson));
    }

    setCartDiscounts(codes) {
        const body = JSON.stringify({
            discount: codes.join(','),
            sections: this.getSectionsToRender().map(section => section.section)
        });

        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), ...{ body } })
            .then((response) => response.json())
            .then((parsedState) => {
                theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-promo', cart: parsedState });
            })
            .catch((error) => {
                console.error(error);
            });
    }

    addProduct(id, cartJson) {
        // All existing gift lines for this variant (added by the progress bar).
        const existing = cartJson.items.filter(
            item => item.variant_id == id && item.properties?._progressbar_promotion
        );

        if (existing.length) {
            // Gift is already in the cart — guarantee it's exactly one unit and stop.
            const totalQty = existing.reduce((sum, i) => sum + (i.quantity || 0), 0);
            if (totalQty > 1) this.capGiftQuantityToOne(existing[0]);
            return;
        }

        // Guard against concurrent adds: rapid cart updates could each fire an
        // add before the first one lands in the cart, adding the gift twice.
        this._inflightGiftAdds = this._inflightGiftAdds || new Set();
        const key = String(id);
        if (this._inflightGiftAdds.has(key)) return;
        this._inflightGiftAdds.add(key);

        const body = JSON.stringify({
            id: id,
            quantity: 1,
            properties: {
                _progressbar_promotion: 'true'
            },
            sections: this.getSectionsToRender().map(section => section.section)
        });
        
        fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig(), ...{ body } })
            .then((response) => response.json())
            .then((parsedState) => {
                theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-promo', cart: parsedState });
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                this._inflightGiftAdds.delete(key);
            });
    }

    capGiftQuantityToOne(giftLine) {
        // Correct any accidental over-quantity (e.g. from an older race) back to 1.
        if (this._cappingGiftQty) return;
        this._cappingGiftQty = true;

        const body = JSON.stringify({
            id: giftLine.key,
            quantity: 1,
            sections: this.getSectionsToRender().map(section => section.section)
        });

        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body } })
            .then((response) => response.json())
            .then((parsedState) => {
                theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-promo', cart: parsedState });
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                this._cappingGiftQty = false;
            });
    }

    removeProduct(id, cartJson) {
        const item = cartJson.items.find(item => item.variant_id == id && item.properties?._progressbar_promotion);
        if(!item) return;

        const body = JSON.stringify({
            id: item.key,
            quantity: 0,
            sections: this.getSectionsToRender().map(section => section.section)
        });
        
        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body } })
            .then((response) => response.json())
            .then((parsedState) => {
                theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-promo', cart: parsedState });
            })
            .catch((error) => {
                console.error(error);
            });
    }

    validateQuantity(event) {
        const target = event.target;
        const inputValue = parseInt(target.value);
        const index = target.getAttribute('data-index');
        this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
    }

    enableLoading(line) {
        const mainCartItems = document.getElementById('mm-main-cart-items') || document.getElementById('CartDrawer-CartItems');
        mainCartItems?.classList.add('cart__items--disabled');


        const cartItemElements = this.querySelectorAll(`#CartItem-${line} .cart-loader`);
        const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .cart-loader`);

        [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => {
        overlay.classList.remove('hidden');
        overlay.closest("mm-cart-remove-button")?.querySelector(".cart-remove-button")?.classList.add('hidden');
        });

        document.activeElement?.blur();
        this.lineItemStatusElement?.setAttribute('aria-hidden', false);
    }

    disableLoading(line) {
        const mainCartItems = document.getElementById('mm-main-cart-items') || document.getElementById('CartDrawer-CartItems');
        mainCartItems?.classList.remove('cart__items--disabled');
    
        const cartItemElements = this.querySelectorAll(`#CartItem-${line} .cart-loader`);
        const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .cart-loader`);
    
        [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => {
          overlay.classList.add('hidden');
          overlay.closest("mm-cart-remove-button")?.querySelector(".cart-remove-button")?.classList.remove('hidden');
        });
    }

    updateLiveRegions(line, message) {
        const lineItemError =
          document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
        if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;
    
        this.lineItemStatusElement?.setAttribute('aria-hidden', true);
    
        const cartStatus =
          document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
        cartStatus?.setAttribute('aria-hidden', false);
    
        setTimeout(() => {
          cartStatus?.setAttribute('aria-hidden', true);
        }, 1000);
    }

    onCartError(errors) {
        this.querySelectorAll('.cart-loader').forEach(overlay => {
          overlay.classList.add('hidden');
          overlay.closest('mm-cart-remove-button')?.querySelector('.cart-remove-button')?.classList.remove('hidden');
        });
        this.showCartError(errors);
    }

    showCartError(message) {
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if(errors) errors.textContent = message;
    }

    getSectionInnerHTML(html, selector) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (!selector) {
            return doc.body.innerHTML.trim();
        }
        const node = doc.querySelector(selector);
        return node?.innerHTML ?? '';
    }
    
    getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'mm-cart-drawer',
            selector: 'mm-cart-items'
          },
          {
            id: 'CartDrawer',
            section: 'mm-cart-drawer',
            selector: '#main-cart-drawer-footer',
          },
          {
            id: 'CartDrawer',
            section: 'mm-cart-drawer',
            selector: '.mm-drawer__header',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
          }
        ];
    }

    readElementAttr(el, attr, isNumber = false) {
        const val = el.getAttribute(attr);
        if (val === null || val.trim() === '') return null;
        return isNumber ? Number(val) : val;
    };
}

customElements.define('mm-cart-items', MMCartItems);


class MMQuantityInput extends HTMLElement {
    constructor() {
        super();
        this.input = this.querySelector('input');
        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));
        this.querySelectorAll('button').forEach((button) =>
            button.addEventListener('click', this.onButtonClick.bind(this))
        );
    }

    quantityUpdateUnsubscriber = undefined;

    connectedCallback() {
        this.validateQtyRules();
        this.quantityUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
    }

    disconnectedCallback() {
        if (this.quantityUpdateUnsubscriber) {
            this.quantityUpdateUnsubscriber();
        }
    }

    onInputChange(event) {
        this.validateQtyRules();
    }

    onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;

        if (event.target.name === 'plus') {
            if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
                this.input.value = this.input.dataset.min;
            } else {
                this.input.stepUp();
            }
        } else {
            this.input.stepDown();
        }

        if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

        if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
            this.input.value = parseInt(this.input.min);
        }
    }

    validateQtyRules() {
        const value = parseInt(this.input.value);
        if (this.input.min) {
            const buttonMinus = this.querySelector(".quantity__button[name='minus']");
            buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
        }
        if (this.input.max) {
            const max = parseInt(this.input.max);
            const buttonPlus = this.querySelector(".quantity__button[name='plus']");
            buttonPlus.classList.toggle('disabled', value >= max);
        }
    }
}

customElements.define('mm-quantity-input', MMQuantityInput);